import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell,
  Send,
  Filter,
  X,
  Search,
  Trash2,
  Eye,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Plus,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Users,
  User,
  Calendar,
  MessageSquare,
  UserCircle
} from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { AdminNotification, NotificationFilters, NotificationPagination } from '../../types';
import { getNotificationCardBg, getNotificationCardBorder, getNotificationCardHeading, getNotificationCardText, getNotificationTypeBadge, getNotificationReadStatus } from '../../utils/statusColors';

interface UserOption {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface UserGroupOption {
  _id: string;
  name: string;
  description?: string;
  staffIds: any[];
  color?: string;
  memberCount?: number;
  totalStaffCount?: number;
}

export const AdminNotifications: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSystemAdmin = user?.role === 'System Administrator';
  const isAdministrator = user?.role === 'Administrator';
  const isAccountOwner = user?.accountRole === 'Account Owner' || user?.accountRole === 'Owner';
  const canBroadcast = isSystemAdmin || isAccountOwner;

  // Helper function to get translation with fallback
  const translate = (key: string, fallback: string): string => {
    const translation = t(key);
    // If translation returns the key itself, use fallback
    return translation === key ? fallback : translation;
  };

  const [view, setView] = useState<'list' | 'create'>('list');
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<AdminNotification | null>(null);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [userGroupOptions, setUserGroupOptions] = useState<UserGroupOption[]>([]);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState<NotificationPagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1
  });

  // Filters
  const [filters, setFilters] = useState<NotificationFilters>({
    userId: '',
    type: '',
    read: '',
    startDate: '',
    endDate: '',
    search: ''
  });

  // Create form state
  const [formData, setFormData] = useState({
    recipientType: 'specific' as 'specific' | 'broadcast',
    userIds: [] as string[],
    groupIds: [] as string[],
    type: 'info' as 'info' | 'success' | 'warning' | 'error',
    title: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const userInputRef = useRef<HTMLInputElement>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError(translate('admin.notAuthenticated', 'Not authenticated'));
        return;
      }

      const queryParams = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.type && { type: filters.type }),
        ...(filters.read !== '' && filters.read !== undefined && { read: String(filters.read) }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.search && { search: filters.search })
      });

      const response = await fetch(`${apiUrl}/api/notifications/admin?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const data = await response.json();

      if (data.success) {
        setNotifications(data.notifications || []);
        if (data.pagination) {
          setPagination(data.pagination);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch notifications');
      }
    } catch (err) {
      console.error('Fetch notifications error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters, apiUrl, t]);

  // Fetch users for dropdown
  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/users/account-users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        const users = data.data.map((ua: any) => ({
          userId: ua.userId,
          firstName: ua.user?.firstName || '',
          lastName: ua.user?.lastName || '',
          email: ua.user?.email || ''
        })).filter((u: UserOption) => u.userId);
        setUserOptions(users);

        // Set active users count for broadcast
        // For Account Owners, this is users in their account
        // For System Admins, we'd need to fetch all users, but for now use account users count
        // The backend will handle the actual broadcast scope
        setActiveUsersCount(users.length);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [apiUrl]);

  // Fetch user groups (StaffGroups) for dropdown
  const fetchUserGroups = useCallback(async () => {
    try {
      setLoadingGroups(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/user-groups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setUserGroupOptions(data.data || []);
      } else {
        console.error('Failed to fetch user groups:', data.message);
      }
    } catch (err) {
      console.error('Failed to fetch user groups:', err);
    } finally {
      setLoadingGroups(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    if (view === 'list') {
      fetchNotifications();
    } else if (view === 'create') {
      fetchUsers();
      fetchUserGroups();
    }
  }, [view, fetchNotifications, fetchUsers, fetchUserGroups]);

  // Debug: Log groups when they're fetched
  useEffect(() => {
    if (userGroupOptions.length > 0) {
      console.log('User groups loaded:', userGroupOptions);
    } else if (view === 'create' && !loadingGroups) {
      console.log('No user groups found. Make sure groups have staff members with user accounts linked.');
    }
  }, [userGroupOptions, loadingGroups, view]);

  useEffect(() => {
    if (view === 'list') {
      fetchNotifications();
    }
  }, [pagination.page, filters]);

  // Handle click outside to close user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showUserDropdown &&
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target as Node) &&
        userInputRef.current &&
        !userInputRef.current.contains(event.target as Node)
      ) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Filter users based on search query
  const filteredUsers = userOptions.filter((u) => {
    const searchLower = userSearchQuery.toLowerCase();
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
    const email = u.email.toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  }).filter((u) => !formData.userIds.includes(u.userId));

  // Filter user groups based on search query
  const filteredUserGroups = userGroupOptions.filter((g) => {
    const searchLower = userSearchQuery.toLowerCase();
    const name = g.name.toLowerCase();
    const description = (g.description || '').toLowerCase();
    return name.includes(searchLower) || description.includes(searchLower);
  }).filter((g) => !formData.groupIds.includes(g._id));

  // Handle filter changes
  const handleFilterChange = (key: keyof NotificationFilters, value: string | boolean | '') => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      userId: '',
      type: '',
      read: '',
      startDate: '',
      endDate: '',
      search: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Create notification
  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.message.trim()) {
      setError(translate('admin.titleAndMessageRequired', 'Title and message are required'));
      return;
    }

    if (formData.recipientType === 'specific' && formData.userIds.length === 0 && formData.groupIds.length === 0) {
      setError(translate('admin.pleaseSelectUserOrEnableBroadcast', 'Please select at least one user or user group, or enable broadcast'));
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(translate('admin.notAuthenticated', 'Not authenticated'));
        return;
      }

      if (formData.recipientType === 'broadcast') {
        // Broadcast to all users (System Admin or Account Owner)
        if (!canBroadcast) {
          setError('Only System Administrators and Account Owners can broadcast notifications');
          return;
        }

        const response = await fetch(`${apiUrl}/api/notifications/broadcast`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: formData.title.trim(),
            message: formData.message.trim(),
            type: formData.type
          })
        });

        const data = await response.json();
        if (data.success) {
          setView('list');
          setFormData({
            recipientType: 'specific',
            userIds: [],
            groupIds: [],
            type: 'info',
            title: '',
            message: ''
          });
          setUserSearchQuery('');
          setShowUserDropdown(false);
          fetchNotifications();
        } else {
          throw new Error(data.message || 'Failed to send notification');
        }
      } else {
        // Send to specific users and/or groups
        const response = await fetch(`${apiUrl}/api/notifications`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userIds: formData.userIds,
            groupIds: formData.groupIds,
            title: formData.title.trim(),
            message: formData.message.trim(),
            type: formData.type
          })
        });

        const data = await response.json();
        if (data.success) {
          setView('list');
          setFormData({
            recipientType: 'specific',
            userIds: [],
            groupIds: [],
            type: 'info',
            title: '',
            message: ''
          });
          setUserSearchQuery('');
          setShowUserDropdown(false);
          fetchNotifications();
        } else {
          throw new Error(data.message || 'Failed to send notification');
        }
      }
    } catch (err) {
      console.error('Create notification error:', err);
      setError(err instanceof Error ? err.message : translate('admin.failedToSendNotification', 'Failed to send notification'));
    } finally {
      setSending(false);
    }
  };

  // Delete notification
  const handleDeleteNotification = async (id: string) => {
    if (!window.confirm(translate('admin.deleteNotificationConfirm', 'Are you sure you want to delete this notification?'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${apiUrl}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        fetchNotifications();
      } else {
        throw new Error(data.message || 'Failed to delete notification');
      }
    } catch (err) {
      console.error('Delete notification error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete notification');
    }
  };

  // Format date
  const formatDate = (dateString: string | Date) => {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  // Get type badge color using app-level utility
  const getTypeBadgeColor = (type: string) => {
    return getNotificationTypeBadge(type as "info" | "success" | "warning" | "error");
  };

  // Get user display name
  const getUserDisplayName = (notification: AdminNotification): string => {
    if (typeof notification.userId === 'object' && notification.userId !== null) {
      const user = notification.userId;
      return `${user.firstName} ${user.lastName}`.trim() || user.email;
    }
    return 'Unknown User';
  };

  // Get user email
  const getUserEmail = (notification: AdminNotification): string => {
    if (typeof notification.userId === 'object' && notification.userId !== null) {
      return notification.userId.email;
    }
    return '';
  };

  // Render list view
  const renderListView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-accent-50 to-primary-50 dark:from-gray-800 dark:to-gray-800 rounded-2xl p-6 border border-accent-100 dark:border-gray-700">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-xl">
              <Bell className="w-7 h-7 text-accent-600 dark:text-accent-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {translate('admin.notificationManagement', 'Notification Management')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-2xl">
                {translate('admin.sendNotificationsDescription', 'Send notifications to individual users or broadcast to all users')}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2.5 rounded-lg transition-all flex items-center gap-2 font-medium ${
                showFilters
                  ? 'bg-accent-600 dark:bg-accent-600 text-white shadow-sm'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              {translate('admin.filterNotifications', 'Filter')}
            </button>
            <button
              onClick={() => {
                setView('create');
                setError(null);
              }}
              className="px-4 py-2.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-500 text-white rounded-lg transition-all flex items-center gap-2 font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" />
              {translate('admin.createNotification', 'Create Notification')}
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/50 rounded-xl p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-1 bg-error-100 dark:bg-error-900/40 rounded-lg">
              <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
            </div>
            <p className="text-sm text-error-900 dark:text-error-200 flex-1">{error}</p>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200 p-1 hover:bg-error-100 dark:hover:bg-error-900/40 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* User filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('admin.recipient', 'Recipient')}
              </label>
              <select
                value={filters.userId || ''}
                onChange={(e) => handleFilterChange('userId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">{translate('admin.all', 'All')}</option>
                {userOptions.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.firstName} {u.lastName} ({u.email})
                  </option>
                ))}
              </select>
            </div>

            {/* Type filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('admin.notificationType', 'Type')}
              </label>
              <select
                value={filters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">{translate('admin.all', 'All')}</option>
                <option value="info">{translate('admin.notificationTypeInfo', 'Info')}</option>
                <option value="success">{translate('admin.notificationTypeSuccess', 'Success')}</option>
                <option value="warning">{translate('admin.notificationTypeWarning', 'Warning')}</option>
                <option value="error">{translate('admin.notificationTypeError', 'Error')}</option>
              </select>
            </div>

            {/* Read status filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('admin.readStatus', 'Read Status')}
              </label>
              <select
                value={filters.read === '' ? '' : String(filters.read)}
                onChange={(e) => handleFilterChange('read', e.target.value === '' ? '' : e.target.value === 'true')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">{translate('admin.all', 'All')}</option>
                <option value="false">{translate('admin.unread', 'Unread')}</option>
                <option value="true">{translate('admin.read', 'Read')}</option>
              </select>
            </div>

            {/* Start date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('admin.from', 'From')}
              </label>
              <input
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* End date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('admin.to', 'To')}
              </label>
              <input
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {translate('common.search', 'Search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder={translate('common.search', 'Search title or message...')}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
            >
              {translate('admin.clearFilters', 'Clear Filters')}
            </button>
          </div>
        </div>
      )}

      {/* Notifications table */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600 dark:text-primary-400 mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Loading notifications...</p>
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-16 border border-gray-200 dark:border-gray-700 text-center shadow-sm">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full w-fit mx-auto mb-4">
            <Bell className="w-16 h-16 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {translate('admin.noNotificationsFound', 'No notifications found')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
            {Object.values(filters).some(f => f !== '' && f !== undefined) 
              ? 'Try adjusting your filters to see more results'
              : 'No notifications have been sent yet. Create your first notification to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.recipient', 'Recipient')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.title', 'Title')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.message', 'Message')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.notificationType', 'Type')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.readStatus', 'Status')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.date', 'Date')}
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                      {translate('admin.actions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {notifications.map((notification) => (
                    <tr key={notification._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getUserDisplayName(notification)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {getUserEmail(notification)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {notification.title}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate">
                          {notification.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(notification.type)}`}>
                          {notification.type === 'info' && <Info className="w-3 h-3 mr-1" />}
                          {notification.type === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {notification.type === 'warning' && <AlertTriangle className="w-3 h-3 mr-1" />}
                          {notification.type === 'error' && <AlertCircle className="w-3 h-3 mr-1" />}
                          {translate(`admin.notificationType${notification.type.charAt(0).toUpperCase() + notification.type.slice(1)}`, notification.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {notification.read ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationReadStatus(true)}`}>
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {translate('admin.read', 'Read')}
                          </span>
                        ) : (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationReadStatus(false)}`}>
                            {translate('admin.unread', 'Unread')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(notification.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedNotification(notification);
                              setShowDetailsModal(true);
                            }}
                            className="p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-all"
                            title={translate('admin.notificationDetails', 'View details')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteNotification(notification._id)}
                            className="p-2 text-error-600 hover:text-error-700 dark:text-error-400 dark:hover:text-error-300 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-all"
                            title={translate('admin.deleteNotification', 'Delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} notifications
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  // Render create view
  const renderCreateView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {translate('admin.createNotification', 'Create Notification')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {translate('admin.sendNotificationsDescription', 'Send notifications to individual users or broadcast to all users')}
          </p>
        </div>
        <button
          onClick={() => {
            setView('list');
            setError(null);
            setFormData({
              recipientType: 'specific',
              userIds: [],
              groupIds: [],
              type: 'info',
              title: '',
              message: ''
            });
            setUserSearchQuery('');
            setShowUserDropdown(false);
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
            <p className="text-sm text-error-900 dark:text-error-200">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleCreateNotification} className="bg-white dark:bg-gray-700 rounded-lg p-6 border border-gray-200 dark:border-gray-600 space-y-6">
        {/* Recipient selection */}
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {translate('admin.recipient', 'Recipient')}
          </label>
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  value="specific"
                  checked={formData.recipientType === 'specific'}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, recipientType: e.target.value as 'specific' | 'broadcast', userIds: [], groupIds: [] }));
                    setUserSearchQuery('');
                    setShowUserDropdown(false);
                  }}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{translate('admin.selectUser', 'Specific User')}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  value="broadcast"
                  checked={formData.recipientType === 'broadcast'}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, recipientType: e.target.value as 'specific' | 'broadcast', userIds: [], groupIds: [] }));
                    setUserSearchQuery('');
                    setShowUserDropdown(false);
                  }}
                  className="mr-2"
                  disabled={!canBroadcast}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {translate('admin.broadcastToAllUsers', 'Broadcast to All Users')}
                  {!canBroadcast && <span className="text-xs text-gray-500 ml-2">(System Admin or Account Owner only)</span>}
                </span>
              </label>
            </div>

            {formData.recipientType === 'specific' && (
              <div className="space-y-4">
                {/* Selected Users and Groups Display */}
                {(formData.userIds.length > 0 || formData.groupIds.length > 0) && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {translate('admin.selectedRecipients', 'Selected Recipients')}
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            userIds: [],
                            groupIds: []
                          }));
                        }}
                        className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {translate('admin.clearAll', 'Clear all')}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-600 min-h-[60px]">
                      {/* Selected Users */}
                      {formData.userIds.map((userId) => {
                        const user = userOptions.find(u => u.userId === userId);
                        if (!user) return null;
                        return (
                          <span
                            key={userId}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-100 dark:bg-accent-900/40 text-accent-800 dark:text-accent-200 rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-shadow"
                          >
                            <User className="w-4 h-4" />
                            <span>{user.firstName} {user.lastName}</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  userIds: prev.userIds.filter(id => id !== userId)
                                }));
                              }}
                              className="ml-1 hover:text-accent-600 dark:hover:text-accent-300 transition-colors"
                              title={translate('admin.removeUser', 'Remove user')}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        );
                      })}
                      {/* Selected Groups */}
                      {formData.groupIds.map((groupId) => {
                        const group = userGroupOptions.find(g => g._id === groupId);
                        if (!group) return null;
                        return (
                          <span
                            key={groupId}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200 rounded-lg text-sm font-medium shadow-sm hover:shadow-md transition-shadow"
                          >
                            <Users className="w-4 h-4" />
                            <span>{group.name}</span>
                            <span className="text-xs opacity-75">({group.staffIds?.length || 0})</span>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  groupIds: prev.groupIds.filter(id => id !== groupId)
                                }));
                              }}
                              className="ml-1 hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
                              title={translate('admin.removeGroup', 'Remove group')}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    {/* Summary */}
                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-md px-3 py-2">
                      <strong>{formData.userIds.length + formData.groupIds.reduce((sum, gid) => {
                        const group = userGroupOptions.find(g => g._id === gid);
                        return sum + (group?.staffIds?.length || 0);
                      }, 0)}</strong> {translate('admin.totalRecipients', 'total recipients')} 
                      {formData.userIds.length > 0 && (
                        <span> • {formData.userIds.length} {translate('admin.directUsers', 'direct user(s)')}</span>
                      )}
                      {formData.groupIds.length > 0 && (
                        <span> • {formData.groupIds.length} {translate('admin.group', 'group(s)')}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* User and Group Selection */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {translate('admin.selectUsersOrGroups', 'Select Users or Groups')}
                  </label>
                  <div className="relative">
                    <input
                      ref={userInputRef}
                      type="text"
                      value={userSearchQuery}
                      onChange={(e) => {
                        setUserSearchQuery(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => {
                        setShowUserDropdown(true);
                      }}
                      placeholder={translate('admin.chooseUserOrGroup', 'Choose users or groups...')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  {showUserDropdown && (
                    <div 
                      ref={userDropdownRef}
                      className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-auto"
                    >
                      {/* Loading State */}
                      {(loadingUsers || loadingGroups) && (
                        <div className="px-4 py-8 text-center">
                          <Loader2 className="w-5 h-5 animate-spin text-primary-500 mx-auto mb-2" />
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {loadingUsers && translate('admin.loadingUsers', 'Loading users...')}
                            {loadingGroups && translate('admin.loadingGroups', 'Loading groups...')}
                          </div>
                        </div>
                      )}

                      {/* Users Section */}
                      {!loadingUsers && filteredUsers.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-600 sticky top-0">
                            {translate('admin.users', 'Users')} ({filteredUsers.length})
                          </div>
                          {filteredUsers.map((u) => (
                            <button
                              key={u.userId}
                              type="button"
                              onClick={() => {
                                if (!formData.userIds.includes(u.userId)) {
                                  setFormData(prev => ({
                                    ...prev,
                                    userIds: [...prev.userIds, u.userId]
                                  }));
                                }
                                setUserSearchQuery('');
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 flex items-center gap-3 transition-colors"
                            >
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
                                <User className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{u.firstName} {u.lastName}</div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{u.email}</div>
                              </div>
                              {formData.userIds.includes(u.userId) && (
                                <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </>
                      )}
                      
                      {/* Groups Section */}
                      {!loadingGroups && filteredUserGroups.length > 0 && (
                        <>
                          <div className={`px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-600 ${filteredUsers.length > 0 ? 'border-t' : ''} sticky top-0`}>
                            {translate('admin.userGroups', 'User Groups')} ({filteredUserGroups.length})
                          </div>
                          {filteredUserGroups.map((g) => (
                            <button
                              key={g._id}
                              type="button"
                              onClick={() => {
                                if (!formData.groupIds.includes(g._id)) {
                                  setFormData(prev => ({
                                    ...prev,
                                    groupIds: [...prev.groupIds, g._id]
                                  }));
                                }
                                setUserSearchQuery('');
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 flex items-center gap-3 transition-colors"
                            >
                              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${g.color ? '' : 'bg-purple-100 dark:bg-purple-900/30'}`} style={g.color ? { backgroundColor: `${g.color}20`, color: g.color } : {}}>
                                <Users className={`w-4 h-4 ${g.color ? '' : 'text-purple-600 dark:text-purple-400'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{g.name}</div>
                                {g.description && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{g.description}</div>
                                )}
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                  {g.staffIds?.length || 0} {translate('admin.members', 'members')}
                                  {g.totalStaffCount && g.totalStaffCount > (g.staffIds?.length || 0) && (
                                    <span className="ml-1 text-warning-600 dark:text-warning-400">
                                      ({g.totalStaffCount - (g.staffIds?.length || 0)} without user accounts)
                                    </span>
                                  )}
                                </div>
                              </div>
                              {formData.groupIds.includes(g._id) && (
                                <CheckCircle className="w-5 h-5 text-success-500 flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </>
                      )}

                      {/* No Results */}
                      {!loadingUsers && !loadingGroups && filteredUsers.length === 0 && filteredUserGroups.length === 0 && (
                        <div className="px-4 py-8 text-center">
                          <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {translate('admin.noUsersOrGroupsFound', 'No users or groups found')}
                          </div>
                          {userSearchQuery && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Try a different search term
                            </div>
                          )}
                          {!userSearchQuery && userGroupOptions.length === 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {translate('admin.noGroupsAvailable', 'No groups available. Create groups in Resources > Groups.')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {formData.recipientType === 'broadcast' && (
              <div className="bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 border border-accent-200 dark:border-accent-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                  <p className="text-sm text-accent-900 dark:text-accent-200">
                    {isSystemAdmin 
                      ? `All active users across all accounts will receive this notification`
                      : `${activeUsersCount} active users in your account will receive this notification`
                    }
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Type */}
        <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {translate('admin.notificationType', 'Notification Type')}
          </label>
          <div className="grid grid-cols-4 gap-3">
            {(['info', 'success', 'warning', 'error'] as const).map((type) => (
              <label
                key={type}
                className={`flex items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition ${
                  formData.type === type
                    ? `${getNotificationCardBg(type)} ${getNotificationCardBorder(type)} border-2`
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-white dark:bg-gray-800'
                }`}
              >
                <input
                  type="radio"
                  name="type"
                  value={type}
                  checked={formData.type === type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as typeof type }))}
                  className="sr-only"
                />
                <div className="text-center">
                  {type === 'info' && <Info className={`w-5 h-5 mx-auto mb-1 ${formData.type === type ? getNotificationCardHeading(type) : 'text-gray-400'}`} />}
                  {type === 'success' && <CheckCircle className={`w-5 h-5 mx-auto mb-1 ${formData.type === type ? getNotificationCardHeading(type) : 'text-gray-400'}`} />}
                  {type === 'warning' && <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${formData.type === type ? getNotificationCardHeading(type) : 'text-gray-400'}`} />}
                  {type === 'error' && <AlertCircle className={`w-5 h-5 mx-auto mb-1 ${formData.type === type ? getNotificationCardHeading(type) : 'text-gray-400'}`} />}
                  <div className={`text-xs font-medium ${formData.type === type ? getNotificationCardHeading(type) : 'text-gray-600 dark:text-gray-400'}`}>
                    {translate(`admin.notificationType${type.charAt(0).toUpperCase() + type.slice(1)}`, type)}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {translate('admin.title', 'Title')} <span className="text-error-500">*</span>
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            maxLength={200}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={translate('admin.enterNotificationTitle', 'Enter notification title')}
            required
          />
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {formData.title.length} / 200 {translate('admin.characters', 'characters')}
          </div>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {translate('admin.message', 'Message')} <span className="text-error-500">*</span>
          </label>
          <textarea
            id="message"
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            maxLength={1000}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder={translate('admin.enterNotificationMessage', 'Enter notification message')}
            required
          />
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {formData.message.length} / 1000 {translate('admin.characters', 'characters')}
          </div>
        </div>

        {/* Preview */}
        {(formData.title || formData.message) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {translate('admin.preview', 'Preview')}
            </label>
            <div className={`p-4 border rounded-lg ${getNotificationCardBg(formData.type)} ${getNotificationCardBorder(formData.type)}`}>
              <h4 className={`font-semibold text-sm mb-2 ${getNotificationCardHeading(formData.type)}`}>
                {formData.title || translate('admin.notificationTitle', 'Notification Title')}
              </h4>
              <p className={`text-sm ${getNotificationCardText(formData.type)}`}>
                {formData.message || translate('admin.notificationMessage', 'Notification message will appear here...')}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={() => {
              setView('list');
              setError(null);
              setFormData({
                recipientType: 'specific',
                userIds: [],
                groupIds: [],
                type: 'info',
                title: '',
                message: ''
              });
              setUserSearchQuery('');
              setShowUserDropdown(false);
            }}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition"
          >
            {translate('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={sending}
            className="px-4 py-2 text-sm bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {translate('admin.sending', 'Sending...')}
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                {translate('admin.sendNotification', 'Send Notification')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  // Render details modal
  const renderDetailsModal = () => {
    if (!selectedNotification) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-700 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {translate('admin.notificationDetails', 'Notification Details')}
              </h3>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedNotification(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Notification Preview Card */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {translate('admin.notificationPreview', 'Notification Preview')}
                </label>
                <div className={`p-4 border rounded-lg ${getNotificationCardBg(selectedNotification.type)} ${getNotificationCardBorder(selectedNotification.type)}`}>
                  <div className="flex items-center gap-2 mb-2">
                    {selectedNotification.type === 'info' && <Info className={`w-4 h-4 ${getNotificationCardHeading(selectedNotification.type)}`} />}
                    {selectedNotification.type === 'success' && <CheckCircle className={`w-4 h-4 ${getNotificationCardHeading(selectedNotification.type)}`} />}
                    {selectedNotification.type === 'warning' && <AlertTriangle className={`w-4 h-4 ${getNotificationCardHeading(selectedNotification.type)}`} />}
                    {selectedNotification.type === 'error' && <AlertCircle className={`w-4 h-4 ${getNotificationCardHeading(selectedNotification.type)}`} />}
                    <h4 className={`font-semibold text-sm ${getNotificationCardHeading(selectedNotification.type)}`}>
                      {selectedNotification.title}
                    </h4>
                  </div>
                  <p className={`text-sm ${getNotificationCardText(selectedNotification.type)} whitespace-pre-wrap`}>
                    {selectedNotification.message}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {translate('admin.recipient', 'Recipient')}
                </label>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {getUserDisplayName(selectedNotification)}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {getUserEmail(selectedNotification)}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {translate('admin.notificationType', 'Type')}
                </label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(selectedNotification.type)}`}>
                  {selectedNotification.type === 'info' && <Info className="w-3 h-3 mr-1" />}
                  {selectedNotification.type === 'success' && <CheckCircle className="w-3 h-3 mr-1" />}
                  {selectedNotification.type === 'warning' && <AlertTriangle className="w-3 h-3 mr-1" />}
                  {selectedNotification.type === 'error' && <AlertCircle className="w-3 h-3 mr-1" />}
                  {translate(`admin.notificationType${selectedNotification.type.charAt(0).toUpperCase() + selectedNotification.type.slice(1)}`, selectedNotification.type)}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {translate('admin.readStatus', 'Read Status')}
                </label>
                {selectedNotification.read ? (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationReadStatus(true)}`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {translate('admin.read', 'Read')}
                  </span>
                ) : (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getNotificationReadStatus(false)}`}>
                    {translate('admin.unread', 'Unread')}
                  </span>
                )}
              </div>

              {selectedNotification.quoteId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Related Quote
                  </label>
                  <a
                    href={`/quotes/${selectedNotification.quoteId}`}
                    className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    View Quote #{selectedNotification.quoteId}
                  </a>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Created
                </label>
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  {formatDate(selectedNotification.createdAt)}
                </div>
              </div>

              {selectedNotification.updatedAt && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Updated
                  </label>
                  <div className="text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(selectedNotification.updatedAt)}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedNotification(null);
                }}
                className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-500 transition"
              >
                {translate('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {view === 'list' ? renderListView() : renderCreateView()}
      {showDetailsModal && renderDetailsModal()}
    </div>
  );
};
